import React, { useState } from 'react';
import { 
  Box, 
  Drawer, 
  AppBar, 
  Toolbar, 
  List, 
  Typography, 
  Divider, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  IconButton,
  Avatar,
  useTheme,
  styled,
  Tooltip,
  Menu,
  MenuItem
} from '@mui/material';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import PetsIcon from '@mui/icons-material/Pets';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationBell from '../NotificationBell/NotificationBell';
import EmailIcon from '@mui/icons-material/Email';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import MedicationIcon from '@mui/icons-material/Medication';

import PaidIcon from '@mui/icons-material/Paid';

import { useAuth } from '../../contexts/AuthContext';
import { usePets } from '../../contexts/PetContext';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import AddToHomeScreen from '../AddToHomeScreen';
import OfflineIndicator from '../OfflineIndicator';
import MobileBottomNavigation from './MobileBottomNavigation';
import { alpha } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import { logger } from '../../services/logger';
import { getSafeAreaTop } from '../../hooks/useSafeArea';

const _log = logger.createScope('SimplifiedNavigation');

const drawerWidth = 280;

const StyledListItem = styled(ListItem)<{ active?: boolean; isSubItem?: boolean }>(({ theme, active, isSubItem: _isSubItem }) => ({
  margin: theme.spacing(0.5, 1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: 'transparent',
  position: 'relative',
  // Glossy bottom accent bar
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: theme.spacing(2),
    right: theme.spacing(2),
    height: active ? '2px' : '0px',
    background: active 
      ? `linear-gradient(90deg, 
          transparent 0%, 
          ${alpha(theme.palette.primary.main, 0.4)} 20%, 
          ${theme.palette.primary.main} 50%, 
          ${alpha(theme.palette.primary.main, 0.4)} 80%, 
          transparent 100%)`
      : 'transparent',
    borderRadius: '2px',
    boxShadow: active 
      ? `0 0 8px ${alpha(theme.palette.primary.main, 0.5)}, 
         0 0 4px ${alpha(theme.palette.primary.main, 0.3)}`
      : 'none',
    transition: 'all 0.3s ease',
  },
  '& .MuiListItemIcon-root': {
    color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  },
  '& .MuiListItemText-primary': {
    color: active ? theme.palette.primary.main : theme.palette.text.primary,
    fontWeight: active ? 600 : 400,
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.action.hover, 0.05),
    '&::after': {
      height: '1px',
      background: `linear-gradient(90deg, 
        transparent 0%, 
        ${alpha(theme.palette.primary.main, 0.2)} 50%, 
        transparent 100%)`,
    },
  },
  transition: 'all 0.2s ease',
}));

// Primary Navigation Items - Option A: Health Intelligence → Financial → Logistics → Resources
const getPrimaryNavItems = (_selectedPetId?: string) => [
  { 
    text: 'Health Insights', 
    icon: <HealthAndSafetyIcon />, 
    path: '/health',
    description: 'Overview, Timeline & Actions'
  },
  { 
    text: 'Care Plan', 
    icon: <MedicationIcon />, 
    path: '/care-plan',
    description: 'Your pet\'s ongoing care & medications'
  },
  { 
    text: 'Pet Expenses', 
    icon: <PaidIcon />, 
    path: '/pet-expenses',
    description: 'Track spending & insurance claims'
  },
  { 
    text: 'Appointments', 
    icon: <CalendarMonthIcon />, 
    path: '/calendar',
    description: 'Upcoming visits & reminders'
  },
  { 
    text: 'Messages', 
    icon: <EmailIcon />, 
    path: '/inbox',
    description: 'Messages with your care team'
  },
  { 
    text: 'Care Team', 
    icon: <MedicalServicesIcon />, 
    path: '/care-team',
    description: "All your pet's providers"
  },
];

// Remove Secondary Navigation Items - integrating everything into primary nav



const SimplifiedNavigation: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);
  const { currentUser, logout } = useAuth();
  const { selectedPet } = usePets();
  const { isAdmin, loading: adminLoading } = useAdminAuth();
  const _theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get navigation items with dynamic pet path
  const primaryNavItems = getPrimaryNavItems(selectedPet?.id);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
    handleMenuClose();
  };

  const isMenuOpen = Boolean(anchorEl);
  const isNotificationMenuOpen = Boolean(notificationAnchorEl);

  const _handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };



  const isActive = (path: string, itemText?: string) => {
    if (itemText === 'Health Insights') {
      return location.pathname === '/health' || location.pathname === '/health-insights';
    }
    return location.pathname === path;
  };

  const drawer = (
    <Box sx={{ overflow: 'auto', display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Primary Navigation */}
      <List component="nav" sx={{ px: 1, py: 2 }}>
        {primaryNavItems.map((item) => (
          <React.Fragment key={item.text}>
            <StyledListItem disablePadding active={isActive(item.path, item.text)} isSubItem={false}>
              <ListItemButton 
                onClick={() => handleNavigation(item.path)}
                sx={{ borderRadius: 1, py: 1.5 }}
              >
                <ListItemIcon sx={{ minWidth: '40px' }}>{item.icon}</ListItemIcon>
                <Box sx={{ flex: 1 }}>
                  <ListItemText 
                    primary={item.text} 
                    secondary={item.description}
                    primaryTypographyProps={{ 
                      fontWeight: isActive(item.path, item.text) ? 600 : 500,
                      fontSize: '0.95rem'
                    }}
                    secondaryTypographyProps={{ 
                      fontSize: '0.8rem',
                      color: 'text.secondary',
                      sx: { 
                        mt: 0.5,
                        lineHeight: 1.3,
                        display: 'block'  // Ensure it's always visible
                      }
                    }}
                  />
                </Box>
              </ListItemButton>
            </StyledListItem>
          </React.Fragment>
        ))}
      </List>

      {/* Admin — bottom of nav, only for admins */}
      {!adminLoading && isAdmin && (
        <>
          <Box sx={{ flex: 1, minHeight: 24 }} />
          <List component="nav" sx={{ px: 1, py: 0 }}>
            <Tooltip title="Admin dashboard — processing costs & usage" placement="right">
              <StyledListItem disablePadding active={location.pathname.startsWith('/admin')} isSubItem={false}>
                <ListItemButton
                  onClick={() => handleNavigation('/admin')}
                  sx={{ borderRadius: 1, py: 1.5 }}
                >
                  <ListItemIcon sx={{ minWidth: '40px' }}>
                    <SecurityIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Admin"
                    secondary="Costs & usage"
                    primaryTypographyProps={{ fontWeight: location.pathname.startsWith('/admin') ? 600 : 500, fontSize: '0.95rem' }}
                    secondaryTypographyProps={{ fontSize: '0.8rem', color: 'text.secondary', sx: { mt: 0.5, lineHeight: 1.3, display: 'block' } }}
                  />
                </ListItemButton>
              </StyledListItem>
            </Tooltip>
          </List>
        </>
      )}
      
      <Divider sx={{ my: 2 }} />
      
      {/* Copyright Notice */}
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          © 2026 Suprie, LLC. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          left: { sm: `${drawerWidth}px` }, // Use left instead of ml for precise positioning
          // Match drawer exactly - use same color values as MuiDrawer theme
          backgroundColor: theme => theme.palette.mode === 'dark' 
            ? `${alpha('#1E293B', 0.95)} !important`  // Exact match to drawer color with !important
            : `${alpha('#FFFFFF', 0.95)} !important`,
          backdropFilter: 'blur(20px)', // Match drawer backdrop
          borderBottom: theme => theme.palette.mode === 'dark' 
            ? '1px solid #334155 !important'  // Match drawer border color
            : '1px solid #E2E8F0 !important',
          color: theme => theme.palette.mode === 'dark' ? '#F8FAFC !important' : '#0F172A !important',
          boxShadow: 'none !important',
          borderLeft: 'none', // Remove any left border that might create a gap
          maxWidth: { lg: `calc(1440px - ${drawerWidth}px)` },
          right: { lg: 'auto' },
          borderRadius: 0, // Remove rounded corners to align with sidebar
          '&.MuiAppBar-root': {
            backgroundColor: theme => theme.palette.mode === 'dark' 
              ? `${alpha('#1E293B', 0.95)} !important`  // Force exact match with !important
              : `${alpha('#FFFFFF', 0.95)} !important`,
          },
          // Apply safe area insets for devices with notches
          ...getSafeAreaTop(),
          '& .MuiToolbar-root': {
            borderRadius: 0, // Ensure toolbar also has no rounded corners
            minHeight: '64px !important', // Set exact height to match sidebar logo area
            height: '64px !important',
            maxHeight: '64px !important',
            paddingLeft: { xs: 'calc(24px + env(safe-area-inset-left))', sm: '24px' },
            paddingRight: { xs: 'calc(24px + env(safe-area-inset-right))', sm: '24px' },
            backgroundColor: 'inherit',
            color: 'inherit',
            display: 'flex !important',
            alignItems: 'center !important',
          }
        }}
      >
        <Toolbar sx={{ 
          minHeight: '64px !important', 
          height: '64px !important',
          maxHeight: '64px !important',
          padding: '0 !important',
          paddingLeft: '24px !important',
          paddingRight: '16px !important',
          display: 'flex !important',
          alignItems: 'center !important',
          // Force mobile alignment
          '& > *': {
            alignSelf: 'center !important'
          }
        }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' }, width: 32, height: 32 }}
          >
            <MenuIcon />
          </IconButton>
          
          {/* Mobile App Title - Left aligned on mobile */}
          <Box sx={{ 
            display: { xs: 'flex', sm: 'none' }, 
            alignItems: 'center', 
            gap: 1
          }}>
            <PetsIcon sx={{ fontSize: '24px', color: 'primary.main' }} />
            <Typography 
              variant="h6" 
              fontWeight="bold" 
              sx={{ 
                fontSize: '1.25rem',
                color: 'text.primary',
                lineHeight: 1
              }}
            >
              Suprie
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />
          
          {/* Right side buttons */}
          <Box sx={{ 
            display: 'flex !important', 
            alignItems: 'center !important', 
            gap: 1,
            order: { xs: 2, sm: 3 },
            height: '32px !important', // Match the height of other header elements
            alignSelf: 'center', // Ensure proper vertical alignment
            marginLeft: { xs: 0, sm: 'auto' } // Only push to right on desktop
          }}>
            {/* Notifications */}
            <NotificationBell size="small" />
            
            {/* User profile */}
            <Tooltip title="Account">
              <IconButton
                edge="end"
                onClick={handleProfileMenuOpen}
                color="inherit"
                sx={{ width: 32, height: 32 }}
              >
                <Avatar 
                  alt={currentUser?.displayName || ''} 
                  src={currentUser?.photoURL || ''} 
                  sx={{ width: 32, height: 32 }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Profile menu */}
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        sx={{
          mt: '45px',
          '& .MuiPaper-root': {
            backdropFilter: 'blur(10px)',
            backgroundColor: theme => alpha(theme.palette.background.paper, 0.85),
            borderRadius: 2,
            border: '1px solid',
            borderColor: theme => alpha(theme.palette.divider, 0.1),
            boxShadow: theme => `0 8px 24px ${alpha(theme.palette.common.black, 0.1)}`
          }
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => { handleMenuClose(); navigate('/profile-settings'); }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
            <Typography>Profile & Settings</Typography>
          </Box>
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LogoutIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
            <Typography>Logout</Typography>
          </Box>
        </MenuItem>
      </Menu>
      
      {/* Notifications menu */}
      <Menu
        anchorEl={notificationAnchorEl}
        open={isNotificationMenuOpen}
        onClose={handleNotificationMenuClose}
        sx={{
          mt: '45px',
          '& .MuiPaper-root': {
            backdropFilter: 'blur(10px)',
            backgroundColor: theme => alpha(theme.palette.background.paper, 0.85),
            borderRadius: 2,
            border: '1px solid',
            borderColor: theme => alpha(theme.palette.divider, 0.1),
            boxShadow: theme => `0 8px 24px ${alpha(theme.palette.common.black, 0.1)}`,
            minWidth: 320,
            maxWidth: 400
          }
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" fontWeight="600">
            Notifications
          </Typography>
        </Box>
        
        {/* Sample notifications - in real app, this would come from state/service */}
        <MenuItem onClick={handleNotificationMenuClose}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
            <Box sx={{ 
              width: 8, 
              height: 8, 
              borderRadius: '50%', 
              bgcolor: 'error.main', 
              mt: 1,
              mr: 2,
              flexShrink: 0
            }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight="500">
                Chewie's vet appointment reminder
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Don't forget about tomorrow's checkup at 2:00 PM
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                2 hours ago
              </Typography>
            </Box>
          </Box>
        </MenuItem>
        
        <MenuItem onClick={handleNotificationMenuClose}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
            <Box sx={{ 
              width: 8, 
              height: 8, 
              borderRadius: '50%', 
              bgcolor: 'warning.main', 
              mt: 1,
              mr: 2,
              flexShrink: 0
            }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight="500">
                Medication reminder
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Time to give Chewie his heartworm medication
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                1 day ago
              </Typography>
            </Box>
          </Box>
        </MenuItem>
        
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography 
            variant="body2" 
            color="primary" 
            sx={{ cursor: 'pointer', textAlign: 'center' }}
            onClick={() => {
              handleNotificationMenuClose();
              navigate('/notifications'); // Navigate to full notifications page if it exists
            }}
          >
            View All Notifications
          </Typography>
        </Box>
      </Menu>
      
      {/* Side drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              borderRadius: 0, // Ensure sidebar has no rounded corners
              // Remove backgroundColor, borderRight, borderColor - let theme handle it
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              borderRight: 'none', // Remove border to eliminate gap
              borderRadius: 0, // Ensure sidebar has no rounded corners
              // Remove backgroundColor, backdropFilter - let theme handle it
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          pl: { xs: 2, md: 3 },
          pr: { xs: 2, md: 3 },
          pt: 'calc(64px + 8px)',
          pb: { xs: 'calc(100px + env(safe-area-inset-bottom))', sm: 0 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: 'transparent',
          maxWidth: { lg: `calc(1440px - ${drawerWidth}px)` },
          mx: 'auto',
          ml: 0,
          borderLeft: 'none',
          overflow: 'auto',
          position: 'relative'
        }}
      >
        <Outlet />
        <AddToHomeScreen />
        <OfflineIndicator />
        <MobileBottomNavigation />
      </Box>
    </Box>
  );
};

export default SimplifiedNavigation;
